// Hybrid user prefs: localStorage for instant reads, DB for cross-browser
// persistence when authenticated. localStorage always reflects the latest known
// value so first paint never blocks on the network.

import { useEffect, useState } from 'react';
import { api } from './api.js';

export const APP_VERSION = 'v0.7.0-alpha';

export type Background = 'void' | 'amethyst' | 'daylight';

export interface ThemeMeta { id: Background; label: string; desc: string; swatch: string }
export const THEMES: ThemeMeta[] = [
  { id: 'void', label: 'Void', desc: 'Pure black with a gold glow — the signature crystal', swatch: 'radial-gradient(circle at 50% 35%,#3a2c12,#070707 70%)' },
  { id: 'amethyst', label: 'Amethyst', desc: 'Deep black with a violet glow', swatch: 'radial-gradient(circle at 50% 35%,#2c2550,#06060e 70%)' },
  { id: 'daylight', label: 'Daylight', desc: 'High-contrast light mode', swatch: 'linear-gradient(135deg,#ffffff,#e7e2d6)' },
];
export type Language = 'fr' | 'en' | 'es';

export type AvatarRenderer = '2d' | 'webgl';

export interface Prefs {
  background: Background;
  language: Language;
  particleDensity: 'low' | 'medium' | 'high';
  cinematicMode: boolean;
  reduceMotion: boolean;
  avatarRenderer: AvatarRenderer;
}

const DEFAULTS: Prefs = {
  background: 'void',
  language: 'fr',
  particleDensity: 'medium',
  cinematicMode: false,
  reduceMotion: false,
  avatarRenderer: '2d',
};

const KEY = 'mdai_prefs';
const CHANNEL = 'mdai-prefs-changed';

const VALID_BG: Background[] = ['void', 'amethyst', 'daylight'];
function coerceBg(bg: unknown): Background {
  return VALID_BG.includes(bg as Background) ? (bg as Background) : 'void';
}

function read(): Prefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    const p = { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Prefs>) };
    p.background = coerceBg(p.background); // map retired themes → void
    return p;
  } catch {
    return DEFAULTS;
  }
}

function write(p: Prefs): void {
  localStorage.setItem(KEY, JSON.stringify(p));
  window.dispatchEvent(new CustomEvent(CHANNEL));
}

async function pushToServer(patch: Partial<Prefs>): Promise<void> {
  if (!localStorage.getItem('mdai_token')) return;
  try {
    await api('/api/prefs', { method: 'PATCH', body: JSON.stringify(patch) });
  } catch {
    // Best-effort. localStorage already has the value.
  }
}

async function pullFromServer(): Promise<void> {
  if (!localStorage.getItem('mdai_token')) return;
  try {
    const { prefs } = await api<{ prefs: Partial<Prefs> }>('/api/prefs');
    if (prefs && Object.keys(prefs).length > 0) {
      write({ ...read(), ...prefs });
    } else {
      // First sync after login: push local prefs up to the server.
      const local = read();
      await pushToServer(local);
    }
  } catch {
    // Stay with localStorage values.
  }
}

let pulled = false;

export function usePrefs(): [Prefs, (patch: Partial<Prefs>) => void] {
  const [prefs, setPrefs] = useState<Prefs>(() => (typeof window === 'undefined' ? DEFAULTS : read()));

  useEffect(() => {
    if (!pulled && typeof window !== 'undefined') {
      pulled = true;
      pullFromServer().then(() => setPrefs(read()));
    }
    const onChange = () => setPrefs(read());
    window.addEventListener(CHANNEL, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(CHANNEL, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  const update = (patch: Partial<Prefs>) => {
    const next = { ...read(), ...patch };
    write(next);
    setPrefs(next);
    if (patch.background !== undefined) applyBackground(next.background); // instant repaint
    void pushToServer(patch);
  };

  return [prefs, update];
}

/** Call after a successful login to sync prefs across the boundary. */
export function refetchPrefs(): void {
  pulled = false;
}

export function applyBackground(bg: Background) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  // bg = base (deep), bg2 = a clearly lighter/tinted top so the gradient reads.
  // ink = foreground text, void = surface base. They flip for light themes.
  const D_INK = '232 226 212', D_VOID = '5 5 5';
  const map: Record<Background, { bg: string; bg2: string; ink: string; void: string; amber: string; particle: string }> = {
    void:      { bg: '#050505', bg2: '#1f1f1f', ink: D_INK, void: D_VOID, amber: '232 185 122', particle: '242 212 162' },
    // Deep black + violet accent — the "user card" look.
    amethyst:  { bg: '#07060f', bg2: '#211c3b', ink: D_INK, void: '7 6 15', amber: '150 130 245', particle: '185 170 255' },
    // Light, high-contrast: dark ink on a near-white surface; darker accents to read on white.
    daylight:  { bg: '#efeae0', bg2: '#ffffff', ink: '38 34 28', void: '250 249 246', amber: '176 120 48', particle: '150 100 40' },
  };
  const v = map[bg] ?? map.void;
  root.style.setProperty('--app-bg', v.bg);
  root.style.setProperty('--app-bg-2', v.bg2);
  root.style.setProperty('--c-ink', v.ink);
  root.style.setProperty('--c-void', v.void);
  root.style.setProperty('--c-amber', v.amber);
  root.style.setProperty('--c-particle', v.particle);
  // A rich radial gradient (concrete colors so the 600ms transition animates).
  document.body.style.background = `radial-gradient(140% 110% at 50% -10%, ${v.bg2} 0%, ${v.bg} 55%)`;
  // Light-mode flag (read by DotHead to switch compositing).
  document.body.classList.toggle('theme-light', bg === 'daylight');
}

export const PARTICLE_COUNTS: Record<Prefs['particleDensity'], number> = {
  low: 600,
  medium: 1200,
  high: 2200,
};
